import type { CaptureOptions } from "../core/types.js";
import type {
  TelemetryLevel,
  TelemetryMessageLevel,
} from "../core/types.js";
import type { TelemetryRuntimeAPI } from "./api.js";

let boundClient: TelemetryRuntimeAPI | undefined;

const UNBOUND_MESSAGE =
  "[telemetry] Client runtime is not bound. Call bindClient(init.api) in entry.client after await telemetry.initClient().";

export function bindClient(api: TelemetryRuntimeAPI): void {
  boundClient = api;
}

export function unbindClient(): void {
  boundClient = undefined;
}

export function getClient(): TelemetryRuntimeAPI {
  if (!boundClient) {
    throw new Error(UNBOUND_MESSAGE);
  }
  return boundClient;
}

export function log(
  level: TelemetryLevel,
  message: string,
  attributes?: Record<string, unknown>,
): void {
  getClient().log(level, message, attributes);
}

export function captureError(error: unknown, options?: CaptureOptions): void {
  getClient().captureError(error, options);
}

export function captureUnhandledRejection(
  reason: unknown,
  options?: CaptureOptions,
): void {
  getClient().captureUnhandledRejection(reason, options);
}

export function captureRouteError(
  error: unknown,
  routeContext: { routeId?: string; route?: string },
  options?: CaptureOptions,
): void {
  getClient().captureRouteError(error, routeContext, options);
}

export function captureMessage(
  message: string,
  level?: TelemetryMessageLevel,
  extra?: Record<string, unknown>,
): void {
  getClient().captureMessage(message, level, extra);
}

export function captureCustom(
  name: string,
  message: string,
  metadata?: Record<string, unknown>,
): void {
  getClient().captureCustom(name, message, metadata);
}

export function addBreadcrumb(
  message: string,
  options?: {
    category?: string;
    level?: TelemetryLevel;
    data?: Record<string, unknown>;
  },
): void {
  getClient().addBreadcrumb(message, options);
}

export async function flush(): Promise<void> {
  await getClient().flush();
}
